import { get } from '@/renderer/utils/get';
import { useUpdateEffect } from '@/renderer/utils/hooks/useUpdateEffect';
import { useLocale } from '@/renderer/utils/locale';
import useMe from '@/renderer/utils/useMe';
import { usePageContext } from '@/renderer/utils/usePageContext';
import { Field, Label, Radio, RadioGroup } from '@headlessui/react';
import {
  ArrowRightIcon,
  ArrowTopRightOnSquareIcon,
  CalendarIcon,
  ChevronDownIcon,
} from '@heroicons/react/16/solid';
import { CheckCircleIcon, InformationCircleIcon, SparklesIcon } from '@heroicons/react/20/solid';
import { ChatBubbleLeftIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { deepEqual } from 'fast-equals';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import Skeleton from '@/components/UI/Skeleton';

import { CrytpoIcon } from './CryptoIcon';
import { OrderErrorBoundary } from './OrderErrorBoundary';
import { PriceInfo } from './PriceInfo';
import PromoBanner from './PromoBanner';
import StripeButton from './StripeButton';
import type { PriceCalculator } from './calculatePrice';
import calculatePrice, { promoDiscountDollars } from './calculatePrice';
import { offerCheckoutIndex } from './offerIdentity';
import { saveCryptoOrderIntent } from './service';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/UI/table';
import MailingListForm from '@/pages/(marketing)/mailing-list/components/MailingListForm';
import { mixpanelTrack } from '@/renderer/utils/stat/mixp';
import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { getProducts } from '../+Page';

const getProductStats: RequestSchema<API.ReleaseStats> = {
  input: '/api/v1/release/stats',
};

const getProductClv: RequestSchema<{ beat: number; total: number }> = {
  input: '/api/v1/release/stats/clv',
};

// Spendable promotional credit (#1622) for the signed-in buyer. Fetched directly
// here rather than read off `me` — `me` is seeded from several auth paths (login,
// refresh, SSR) that don't carry it, so it wouldn't reliably reach checkout. The
// charged amount is still recomputed server-side; this only keeps the shown price
// honest.
// A promotional DISCOUNT grant (#1689) applicable to the product in view — a
// one-shot percentage or fixed-amount concession the order page nets out of the
// shown price. Present only when the buyer has a matching grant.
type PromoDiscount = {
  kind: 'pct' | 'amount';
  pct: number | null;
  amount: number | null;
  expiresAt?: string | null;
};

const getSpendableCredit: RequestSchema<{
  total: number;
  expiresAt?: string | null;
  discount?: PromoDiscount | null;
}> = {
  input: '/api/v1/order/credit/me',
};
import {
  OFFER_MATRIX_KEYS,
  OFFER_MATRIX_MAP,
  OFFER_MATRIX_TITLES,
  getFrequencyLabels,
  isOfferSelectable,
  matrixCollisions,
  matrixFromOffers,
} from './productHelpers';

type MPO = API.MembershipProductOffer;

const BASE_STATS: API.MembershipProductStats = {
  canceled: 0,
  loss: 0,
  noAction: 0,
  pending: 0,
  played: 0,
  push: 0,
  units: 0,
  win: 0,
  buyable: 0,
  charged: 0,
  roi: 0,
  total: 0,
};

const BASE_OFFER: MPO = {
  id: 0,
  ids: [0],
  plays: 0,
  price: 0,
};

const disclaimer = {
  href: '#',
  summary:
    'It should be expected that lines will move within seconds of plays being released. Despite our long-term history of winning (55-56% across thousands of plays), we are not immune to variance, poor streaks, or losing seasons. Please manage your bankroll and expectations accordingly. Releases may be viewed as "sharp" action that could lead to reduced limits or account closure at certain sportsbooks. Proper bankroll size, available outs, time and skills are needed to be successful and justify the cost of this service.',
};

function toFixed(num?: number) {
  if (typeof num !== 'number') {
    return num;
  }

  return (num || 0).toFixed(2);
}

export const ProductPageSkeleton = () => (
  <div className="grid grid-cols-1 lg:grid-cols-7 lg:items-start lg:gap-x-10">
    {/* Left: product card */}
    <div className="w-full lg:col-span-4">
      <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
        {/* Header */}
        <div className="px-5 py-6 sm:px-7">
          <div className="flex items-center gap-3">
            <Skeleton loading as="div" className="size-10 shrink-0 rounded-xl">
              <span>icon</span>
            </Skeleton>
            <div className="space-y-2">
              <Skeleton loading>
                <span className="font-bold text-xl">Product title</span>
              </Skeleton>
              <Skeleton loading>
                <span className="text-xs">Subtitle text</span>
              </Skeleton>
            </div>
          </div>
        </div>

        {/* Offer selector */}
        <hr className="mx-5 my-0 border-zinc-950/10 sm:mx-7 dark:border-white/10" />
        <div className="px-5 pt-5 sm:px-7">
          <Skeleton loading>
            <span className="font-medium text-xs">Select offer</span>
          </Skeleton>
          <div className="mt-2 mb-6 grid grid-cols-2 gap-3">
            {[...Array(2)].map((_, i) => (
              <Skeleton key={i} loading as="div" className="h-20 rounded-sm">
                <span>option</span>
              </Skeleton>
            ))}
          </div>
        </div>

        {/* Includes */}
        <hr className="mx-5 my-0 border-zinc-950/10 sm:mx-7 dark:border-white/10" />
        <div className="px-5 py-5 sm:px-7">
          <ul className="flex flex-col gap-y-3">
            {[...Array(4)].map((_, i) => (
              <li key={i} className="flex items-start gap-x-3">
                <Skeleton loading>
                  <span className="text-sm">
                    {i % 2 === 0 ? 'Included item text goes here' : 'Another included feature'}
                  </span>
                </Skeleton>
              </li>
            ))}
          </ul>
        </div>

        {/* Price + buy */}
        <hr className="mx-5 my-0 border-zinc-950/10 sm:mx-7 dark:border-white/10" />
        <div className="px-5 py-6 sm:px-7">
          <Skeleton loading>
            <span className="font-bold text-4xl">$299</span>
          </Skeleton>
          <Skeleton loading>
            <p className="mb-4 text-xs text-zinc-500">Full season · One-time payment</p>
          </Skeleton>
          <div className="mt-4 flex flex-col gap-3">
            <Skeleton loading as="div" className="h-12 w-full rounded-xl">
              <span>Buy button</span>
            </Skeleton>
          </div>
        </div>

        {/* Mailing list footer */}
        <hr className="mx-5 my-0 border-zinc-950/10 sm:mx-7 dark:border-white/10" />
        <div className="bg-zinc-50 px-5 py-5 sm:px-7 dark:bg-zinc-800/50">
          <Skeleton loading>
            <p className="text-xs text-zinc-600">Not ready to sign up?</p>
          </Skeleton>
          <Skeleton loading>
            <p className="mt-1 text-xs text-zinc-400">Get free picks and service announcements.</p>
          </Skeleton>
          <Skeleton loading as="div" className="mt-3 h-8 w-full rounded-lg">
            <span>email input</span>
          </Skeleton>
        </div>
      </div>
    </div>

    {/* Right: track record + FAQ */}
    <div className="mt-10 lg:col-span-3 lg:mt-0">
      <div className="overflow-hidden rounded-2xl border border-zinc-200 dark:border-zinc-700">
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-5 sm:px-7">
          <div className="space-y-1.5">
            <Skeleton loading>
              <span className="font-semibold text-zinc-950">Track record</span>
            </Skeleton>
            <Skeleton loading>
              <span className="text-xs text-zinc-400">CFB sides &amp; totals · last 3 seasons</span>
            </Skeleton>
          </div>
        </div>

        {/* 2×2 stat grid */}
        <hr className="mx-5 my-0 border-zinc-950/10 sm:mx-7 dark:border-white/10" />
        <div className="grid grid-cols-2 gap-x-4 px-5 py-4 sm:px-7">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="py-2">
              <Skeleton loading>
                <p className="font-semibold text-[10px] uppercase tracking-widest">Label</p>
              </Skeleton>
              <Skeleton loading>
                <p className="mt-1 font-bold text-xl">000–000</p>
              </Skeleton>
            </div>
          ))}
        </div>

        {/* Season table */}
        <hr className="mx-5 my-0 border-zinc-950/10 sm:mx-7 dark:border-white/10" />
        <div className="px-5 py-4 sm:px-7">
          <table className="w-full">
            <tbody className="divide-y divide-zinc-50 dark:divide-zinc-800">
              {[...Array(3)].map((_, i) => (
                <tr key={i}>
                  <td className="py-1.5">
                    <Skeleton loading>
                      <span className="text-xs text-zinc-500">Season 2024</span>
                    </Skeleton>
                  </td>
                  <td className="py-1.5 text-right">
                    <Skeleton loading>
                      <span className="text-xs font-semibold">00–00</span>
                    </Skeleton>
                  </td>
                  <td className="py-1.5 text-right">
                    <Skeleton loading>
                      <span className="text-xs font-semibold">00.0%</span>
                    </Skeleton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Betstamp footer */}
        <hr className="mx-5 my-0 border-zinc-950/10 sm:mx-7 dark:border-white/10" />
        <div className="px-5 py-3 sm:px-7">
          <Skeleton loading>
            <span className="text-xs text-zinc-400">
              Results independently verified by betstamp →
            </span>
          </Skeleton>
        </div>
      </div>
    </div>
  </div>
);

const ProductPage = ({
  product,
  IconComponent,
  isLoading,
}: {
  product: API.MembershipProduct<true>;
  IconComponent?: React.ComponentType<React.ComponentPropsWithoutRef<'svg'>>;
  isLoading: boolean;
}) => {
  const pageContext = usePageContext();
  const { locale, formatNumber } = useLocale();

  const balance = useMe()?.balance || 0;
  const discount = useMe()?.discount || 0;
  const meId = useMe()?.id;

  // Spendable promotional credit (#1622) for a signed-in buyer. Shown as applied
  // at checkout and netted out of the displayed price (promo spends before
  // refundable balance, matching the server split). Fetched directly here (not
  // read off `me`, which is seeded from auth paths that don't carry it) and only
  // once the buyer is known — the endpoint requires auth. The Stripe amount is
  // recomputed server-side, so this is display-only.
  // Pass the product in view so the response also carries any applicable
  // promotional discount grant (#1689), scoped to that product. The query is part
  // of the request options (and the cache key), so a different product refetches.
  const productIdForCredit = product?.id;
  const promoCreditRequest = useRequest(getSpendableCredit, {
    manual: true,
    skipRefresh: true,
    cache: { maxAge: 60 * 1000 },
    query: productIdForCredit ? { productId: productIdForCredit } : undefined,
  });
  const runPromoCredit = promoCreditRequest.run;
  useEffect(() => {
    if (meId) {
      runPromoCredit();
    }
  }, [meId, productIdForCredit, runPromoCredit]);
  // Show the credit block when there is spendable credit OR an applicable discount.
  const promoCredit =
    promoCreditRequest.data &&
    (promoCreditRequest.data.total > 0 || promoCreditRequest.data.discount)
      ? promoCreditRequest.data
      : null;
  const promoDiscount = promoCreditRequest.data?.discount ?? null;

  const offers = product?.offers;
  const addOnProductId = useMemo(() => {
    return offers?.find((offer) => offer.addOn?.productId)?.addOn?.productId || 0;
  }, [offers]);

  const addOnProductRequest = useRequest(getProducts, {
    manual: !addOnProductId,
    query: { data: { id: addOnProductId } },
    skipRefresh: true,
  });

  const addOnProduct = addOnProductRequest.data?.[0];

  const productId = product?.id;
  // Optional range toggle — set `statsRanges: ["all", 3]` in product.ui to show range buttons
  // Values: "all" = all time, number N = last N seasons
  const statsRangeOptions: Array<'all' | number> = (product?.ui as any)?.statsRanges ?? ['all'];
  const [statsRange, setStatsRange] = useState<'all' | number>(statsRangeOptions[0] ?? 'all');

  // `?key=<offer.key>` names one offer explicitly — the only way a hidden offer is
  // reachable from this page, and what makes "arrived deliberately" distinguishable
  // from "the option matrix picked it" (#2104).
  const admittedOfferKey = pageContext?.urlParsed?.search?.key;

  /** Offers the buyer may actually be routed onto: everything not hidden, plus an explicitly named one. */
  const isSelectable = useCallback(
    (offer: MPO) => isOfferSelectable(offer, admittedOfferKey),
    [admittedOfferKey],
  );

  const urlSelectedIndex = useMemo(() => {
    if (admittedOfferKey) {
      const foundIdx = product?.offers.findIndex(
        (productOffer) => productOffer.key === admittedOfferKey,
      );

      // findIndex returns -1 for "no such key", and -1 used to fall straight
      // through this `!== undefined` check: `offers[-1]` is undefined, so an
      // unknown ?key= rendered the page at $0 against no offer at all.
      if (foundIdx !== undefined && foundIdx !== -1) {
        return foundIdx;
      }
    }

    // Look for an offer marked as default
    const defaultIdx = product?.offers.findIndex(
      (offer) => offer?.ui?.isDefault && isOfferSelectable(offer, admittedOfferKey),
    );
    if (defaultIdx !== undefined && defaultIdx !== -1) {
      return defaultIdx;
    }

    // Never open on a hidden offer: index 0 is only a safe default when it is
    // one the buyer is allowed to be routed onto.
    const firstSelectable = product?.offers.findIndex((offer) =>
      isOfferSelectable(offer, admittedOfferKey),
    );

    return firstSelectable !== undefined && firstSelectable !== -1 ? firstSelectable : 0;
  }, [product?.offers, admittedOfferKey]);

  const [selectedIndex, setSelectedIndex] = useState<number>(urlSelectedIndex);

  const variety = offers?.[selectedIndex];

  // "Packages" mode: the offers are mutually-exclusive bundles (e.g. CFB
  // FBS + FCS / FBS only / FCS only) that differ on more than one matrix key and
  // so can't be cleanly auto-derived into a single selector. Render them as one
  // radio of package cards instead, ordered by ui.tier.
  //
  // Forced on as well when the option matrix would be LOSSY — when two selectable
  // offers agree on every matrix key and so collapse onto one option, leaving the
  // other unreachable and selection resolving to whichever `getBestOfferMatch`
  // returns. `ids` is the usual reason (it is not a matrix key). One card per offer
  // is the only faithful rendering, so we fall back to it rather than drawing a
  // selector that silently cannot express the catalogue (#2104).
  const lossyMatrixPairs = useMemo(
    () => matrixCollisions(offers, isSelectable),
    [offers, isSelectable],
  );
  const matrixIsLossy = lossyMatrixPairs.length > 0;
  const packagesMode = !!(product?.ui as any)?.packages || matrixIsLossy;

  // Loudly, not silently: a collision means the catalogue grew an offer the
  // matrix cannot express, and someone should give it a distinguishing key.
  useEffect(() => {
    if (!matrixIsLossy) {
      return;
    }
    console.warn(
      `[order] product ${product?.id}: offers ${JSON.stringify(lossyMatrixPairs)} are indistinguishable in the option matrix; rendering one card per offer`,
    );
    mixpanelTrack('order/client:lossy-matrix', {
      product_id: product?.id,
      pairs: JSON.stringify(lossyMatrixPairs),
    });
  }, [matrixIsLossy, lossyMatrixPairs, product?.id]);

  const packageOffers = useMemo(() => {
    if (!packagesMode || !offers) {
      return [];
    }
    return (
      offers
        .map((offer, index) => ({ offer, index }))
        // `index` is the offer's position in product.offers and is what selection
        // and checkout both use, so hidden offers are filtered out AFTER mapping.
        .filter(({ offer }) => isSelectable(offer))
        .sort((a, b) => ((a.offer.ui as any)?.tier ?? 99) - ((b.offer.ui as any)?.tier ?? 99))
    );
  }, [packagesMode, offers, isSelectable]);

  // gameType + sportId stat scoping. Prefer the selected offer (so CFB packages can
  // each scope the track record — FBS vs FCS via release gameType) and fall back to
  // the product-level setting.
  // - statsGameTypes (e.g. ["fcs"] or ["normal","extra","added"]) restricts the track
  //   record by release gameType. gameType is season-independent, so this is robust
  //   across the per-season service rotation.
  // - statsSportId queries releases by sportId instead of message.productIds (use when
  //   historical releases lack messageIds, e.g. CFB).
  const statsGameTypes: string[] | undefined =
    (variety?.ui as any)?.statsGameTypes ?? (product?.ui as any)?.statsGameTypes ?? undefined;
  const statsSportId: number | undefined =
    (variety?.ui as any)?.statsSportId ?? (product?.ui as any)?.statsSportId ?? undefined;

  // When the selected offer scopes the track record (e.g. Preseason only, or a CFB
  // FBS/FCS package), filter stats/CLV accordingly and relabel. Prefer seasonType
  // (e.g. 'preseason') since the preseason service rotates each season. A
  // statsServiceIds fallback remains for any service-scoped offer, and a per-offer
  // statsGameTypes scopes by release gameType (FBS vs FCS).
  const coverageServiceIds: number[] | null = useMemo(() => {
    const ids = (variety?.ui as any)?.statsServiceIds;
    return Array.isArray(ids) && ids.length ? ids : null;
  }, [variety]);
  const coverageSeasonType: string | null = (variety?.ui as any)?.statsSeasonType ?? null;
  // Only treat gameType as a coverage filter when it comes from the selected offer —
  // product-level statsGameTypes keep their existing (non-coverage) labelling.
  const coverageGameTypes: string[] | null = useMemo(() => {
    const gts = (variety?.ui as any)?.statsGameTypes;
    return Array.isArray(gts) && gts.length ? gts : null;
  }, [variety]);
  const coverageLabel: string | null = (variety?.ui as any)?.statsLabel ?? null;
  const coverageActive = !!coverageServiceIds || !!coverageSeasonType || !!coverageGameTypes;
  const coverageSportIds = product?.sportIds ?? [];

  // A range toggle (statsRanges with more than one option) only makes sense with
  // data spanning multiple seasons. The default `message.productIds` scoping is
  // season-specific — product IDs rotate each season, so a message is only tagged
  // with the current season's product — which means it returns just the current
  // season and the "All time"/"Last N" toggle can never change the record. When no
  // explicit coverage/sportId scope is configured, fall back to the product's
  // sportIds so historical seasons are included and the toggle actually filters.
  const rangeNeedsAllSeasons = statsRangeOptions.length > 1 && coverageSportIds.length > 0;

  // FAQs can be scoped to the selected coverage:
  // - `onlyPreseason` / `notPreseason` toggle with the Preseason-only offer.
  // - `onlyPackages: [...]` shows an item only when the selected offer's
  //   `ui.packageKey` (e.g. 'fbs', 'fcs', 'fbs-fcs') is in the list. Lets each CFB
  //   package show its own "How many plays will I get?" answer.
  const isPreseasonSelected = !!(variety?.ui as any)?.isPreseasonOnly;
  const packageKey: string | null = (variety?.ui as any)?.packageKey ?? null;
  // Coverage term shown under the price. Defaults to "Full season" but a product
  // or offer can override it (e.g. CFB is Weeks 0–7, not a full season), plus an
  // optional note beneath (e.g. "Bowls sold separately"). Offer-level wins.
  const termLabel: string =
    (variety?.ui as any)?.termLabel ?? (product?.ui as any)?.termLabel ?? 'Full season';
  // The note under the price (e.g. "Bowls sold separately"). A per-offer override
  // wins (including an explicit "" to suppress it). Otherwise the product-level
  // note is inherited ONLY for offers that share the product's own duration — an
  // offer that defines its own `termLabel` is a distinct duration (e.g. the CFB
  // "Weeks 0-1" intro), so the product default must not leak onto it.
  const termNote: string | null = (() => {
    const ownNote = (variety?.ui as any)?.termNote;
    if (ownNote != null) {
      return ownNote;
    }
    if ((variety?.ui as any)?.termLabel != null) {
      return null;
    }
    return (product?.ui as any)?.termNote ?? null;
  })();
  const visibleFaqs = useMemo(
    () =>
      (product?.ui?.faqs ?? []).filter((faq: any) => {
        if (faq?.onlyPreseason && !isPreseasonSelected) {
          return false;
        }
        if (faq?.notPreseason && isPreseasonSelected) {
          return false;
        }
        if (
          Array.isArray(faq?.onlyPackages) &&
          (!packageKey || !faq.onlyPackages.includes(packageKey))
        ) {
          return false;
        }
        return true;
      }),
    [product?.ui?.faqs, isPreseasonSelected, packageKey],
  );

  const productStatsRequest = useRequest(
    getProductStats,
    useMemo(
      () => ({
        manual: !productId,
        query: {
          page: { limit: 100 },
          data: {
            ...(coverageSeasonType
              ? { sportId$in: coverageSportIds, seasonType: [coverageSeasonType] }
              : coverageServiceIds
                ? { 'message.serviceIds': coverageServiceIds }
                : statsSportId
                  ? { sportId$in: [statsSportId] }
                  : rangeNeedsAllSeasons
                    ? { sportId$in: coverageSportIds }
                    : { 'message.productIds': [productId] }),
            playType: ['side', 'total', 'prop', 'future', 'not-available'],
            ...(statsGameTypes ? { gameType: statsGameTypes } : {}),
          },
        },
        skipRefresh: true,
        cache: { maxAge: 5 * 60 * 1000 },
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        productId,
        statsSportId,
        JSON.stringify(statsGameTypes),
        JSON.stringify(coverageServiceIds),
        coverageSeasonType,
        JSON.stringify(coverageSportIds),
        rangeNeedsAllSeasons,
      ],
    ),
  );

  const productClvRequest = useRequest(
    getProductClv,
    useMemo(
      () => ({
        manual: !productId,
        query: {
          data: {
            // CLV endpoint accepts `sportId` (not `sportId$in`) — use it directly
            ...(coverageSeasonType
              ? { sportId: coverageSportIds, seasonType: [coverageSeasonType] }
              : coverageServiceIds
                ? { 'message.serviceIds': coverageServiceIds }
                : statsSportId
                  ? { sportId: [statsSportId] }
                  : rangeNeedsAllSeasons
                    ? { sportId: coverageSportIds }
                    : { 'message.productIds': [productId] }),
            ...(statsGameTypes ? { gameType: statsGameTypes } : {}),
          },
        },
        skipRefresh: true,
        cache: { maxAge: 5 * 60 * 1000 },
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        productId,
        statsSportId,
        JSON.stringify(statsGameTypes),
        JSON.stringify(coverageServiceIds),
        coverageSeasonType,
        JSON.stringify(coverageSportIds),
        rangeNeedsAllSeasons,
      ],
    ),
  );

  // Compute aggregate W/L/units from the stats endpoint
  const dynamicStats = useMemo(() => {
    const statsData = productStatsRequest.data;
    if (!statsData?.result) {
      return null;
    }
    const wins = Number((statsData.result as any)?.win?.total ?? 0);
    const losses = Number((statsData.result as any)?.loss?.total ?? 0);
    const pushes = Number((statsData.result as any)?.push?.total ?? 0);
    const unitsWin = Number((statsData.result as any)?.win?.units ?? 0);
    const unitsLoss = Number((statsData.result as any)?.loss?.units ?? 0);
    const unitsPush = Number((statsData.result as any)?.push?.units ?? 0);
    const units = unitsWin + unitsLoss + unitsPush;
    const total = wins + losses + pushes;
    // If no data came back (e.g. messages.productIds not populated in dev), return null
    if (total === 0) {
      return null;
    }
    return { wins, losses, pushes, units, total };
  }, [productStatsRequest.data]);

  const dynamicClv = useMemo(() => {
    const clvData = productClvRequest.data;
    if (!clvData || clvData.total === 0) {
      return null;
    }
    return Math.round((clvData.beat / clvData.total) * 1000) / 10; // one decimal
  }, [productClvRequest.data]);

  // Per-season rows from stats endpoint (for the breakdown table)
  const dynamicSeasonRows = useMemo(() => {
    // Only use dynamic rows if we have real data (dynamicStats non-null means total > 0)
    const statsData = productStatsRequest.data;
    if (!statsData?.seasonId || !dynamicStats) {
      return null;
    }

    const rows: Array<{
      label: string;
      wins: number;
      losses: number;
      units: number;
      sortDate: number;
    }> = [];

    for (const key in statsData.seasonId as any) {
      const entry = (statsData.seasonId as any)[key];
      const seasonMeta = entry?.meta?.season
        ? (Object.values(entry.meta.season as Record<string, any>)[0] as any)
        : null;
      if (!seasonMeta) {
        continue;
      }
      const label = seasonMeta.name || String(seasonMeta.id || key);
      const wins = Number(entry?.stats?.win?.total ?? 0);
      const losses = Number(entry?.stats?.loss?.total ?? 0);
      const _pushes = Number(entry?.stats?.push?.total ?? 0);
      const unitsWin = Number(entry?.stats?.win?.units ?? 0);
      const unitsLoss = Number(entry?.stats?.loss?.units ?? 0);
      const unitsPush = Number(entry?.stats?.push?.units ?? 0);
      const units = unitsWin + unitsLoss + unitsPush;
      const sortDate = Date.parse(seasonMeta.startDate || seasonMeta.endDate || '') || 0;
      if (wins + losses === 0) {
        continue;
      }
      rows.push({ label, wins, losses, units, sortDate });
    }

    rows.sort((a, b) => b.sortDate - a.sortDate);
    return rows.length > 0 ? rows : null;
  }, [productStatsRequest.data, dynamicStats]);

  const isSubscription = variety?.time?.subscription?.interval;
  const isAnnualSubscription = variety?.time?.subscription?.interval === '1 years';

  const stats = useMemo(() => {
    if (product?.stats) {
      return {
        ...product.stats,
        played:
          product.stats.played ||
          (+product.stats.loss || 0) +
            (+product.stats.pending || 0) +
            (+product.stats.push || 0) +
            (+product.stats.win || 0) -
            (product.stats.uncharged || 0),
      };
    }

    return undefined;
  }, [product?.stats]);

  const calcPrice = useMemo(() => {
    return calculatePrice({
      discount,
      stats: stats || BASE_STATS,
      variety: variety || BASE_OFFER,
    });
  }, [discount, stats, variety]);

  // Sectioned package layout. Group the package offers by their section heading
  // (offer.ui.termHeading) and render every group on one page, so a buyer sees the
  // main packages and the short intro prices together. Each offer stays a
  // selectable card in a single radio, so selection/purchase behavior is unchanged.
  //
  // Offers WITHOUT a termHeading form one unlabelled group (the main packages) —
  // rendered as a plain card grid with no header, so nothing over-promises a "full
  // season". Offers WITH a termHeading (e.g. the "2-week intro") form a labelled
  // group; the header appends "· {termLabel}" only when that group's offers all
  // share one duration. Grouping this way (not by termLabel) lets packages of
  // different durations sit in the same block — e.g. FBS (Weeks 0–7) beside FCS
  // (Weeks 0–5) — while each card's price footer still shows its own real weeks.
  // Cards are titled by `ui.coverage` (e.g. "FBS + FCS"), falling back to the title.
  //
  // A product whose offers have no termHeading renders as a single unlabelled
  // section: the original flat package grid, unchanged.
  const priceOf = useCallback(
    (offer: MPO) => calculatePrice({ discount, stats: stats || BASE_STATS, variety: offer }).price,
    [discount, stats],
  );

  const durationSections = useMemo(() => {
    type Section = {
      heading: string | null;
      // termLabel shared by every offer in the group, else null (mixed durations).
      sharedTerm: string | null;
      offers: typeof packageOffers;
      maxPrice: number;
    };
    if (!packagesMode || packageOffers.length === 0) {
      return [] as Section[];
    }
    const productTerm = (product?.ui as any)?.termLabel ?? 'Full season';
    // Key by heading; heading-less offers share the empty-string key (one group).
    const groups = new Map<string, Section>();
    for (const po of packageOffers) {
      const heading = (po.offer.ui as any)?.termHeading ?? null;
      const termLabel = (po.offer.ui as any)?.termLabel ?? productTerm;
      const price = priceOf(po.offer);
      const existing = groups.get(heading ?? '');
      if (existing) {
        existing.offers.push(po);
        existing.maxPrice = Math.max(existing.maxPrice, price);
        if (existing.sharedTerm !== termLabel) {
          existing.sharedTerm = null;
        }
      } else {
        groups.set(heading ?? '', {
          heading,
          sharedTerm: termLabel,
          offers: [po],
          maxPrice: price,
        });
      }
    }
    // Most-valuable group first — the main packages above the short intro.
    return Array.from(groups.values()).sort((a, b) => b.maxPrice - a.maxPrice);
  }, [packagesMode, packageOffers, product?.ui, priceOf]);

  const getBestOfferMatch = useMemo(() => {
    return (
      key: keyof MPO,
      map: Map<string, { option: MPO[keyof MPO]; indexes: Set<number> }>,
      next: string,
      proposedOffer?: MPO,
    ) => {
      const matrix = map.get(next);
      const nextAllowedIndexes = Array.from(matrix?.indexes || []);

      if (nextAllowedIndexes.length === 0) {
        return 0;
      }

      if (nextAllowedIndexes.length === 1) {
        return nextAllowedIndexes[0];
      }

      let bestMatch = 0;
      let bestMatchCount = -1;

      if (offers) {
        for (const offerIdx in offers) {
          const offer = offers[offerIdx];

          // Never resolve a click onto an offer the buyer cannot be routed onto.
          // Without this a hidden campaign offer could win the best-match scan
          // even though it rendered no option of its own (#2104).
          if (!isSelectable(offer)) {
            continue;
          }

          const value = get(offer, key);

          if (
            value === matrix?.option ||
            (typeof value === 'object' && deepEqual(value, matrix?.option))
          ) {
            let matchCount = 0;

            if (!proposedOffer) {
              return +offerIdx;
            }

            for (const matrixKey of OFFER_MATRIX_KEYS.values()) {
              const offerValue = get(offer, matrixKey);
              const proposedValue = get(proposedOffer, matrixKey);

              if (
                offerValue === proposedValue ||
                (typeof offerValue === 'object' && deepEqual(offerValue, proposedValue))
              ) {
                matchCount++;
              }
            }

            if (matchCount > bestMatchCount) {
              bestMatchCount = matchCount;
              bestMatch = +offerIdx;
            }
          }
        }
      }

      return bestMatch;
    };
  }, [offers, isSelectable]);

  useUpdateEffect(() => {
    // Index 0 may be a hidden offer; `urlSelectedIndex` is the first one the buyer
    // is allowed to land on (and honours ?key= / ui.isDefault) (#2104).
    setSelectedIndex(urlSelectedIndex);
  }, [product]);

  const triggerSetSelections = useMemo(() => {
    return (key: keyof MPO, map: Map<string, { option: MPO[keyof MPO]; indexes: Set<number> }>) =>
      (next: string) => {
        mixpanelTrack('order/client:selection', {
          product_id: product?.id,
          selection_key: key,
          selection_next: next,
        });
        setSelectedIndex(getBestOfferMatch(key, map, next, variety));
      };
  }, [variety, getBestOfferMatch, product?.id]);

  const matrices = useMemo(() => {
    // Hidden offers take no part in public option rendering or resolution (#2104).
    return matrixFromOffers(product, isSelectable);
  }, [product, isSelectable]);

  const hasBettingTalkExtra = useMemo(() => {
    const apid = variety?.addOn?.productId;

    if (apid) {
      return product?.products?.[apid]?.tags?.includes('betting-talk') || apid === 49;
    }

    if (variety?.combos) {
      for (const { productId: pid } of variety.combos) {
        if (pid) {
          return product?.products?.[pid]?.tags?.includes('betting-talk') || pid === 49;
        }
      }
    }
  }, [variety, product?.products]);

  /** Something of a sprintf-type function for templating strings */
  // Pre-computed stats context for {stats.*} template variables in ui.includes and elsewhere.
  // Uses dynamic DB stats when available, falls back to product.ui.resultSummaries.
  const statsContext = useMemo(() => {
    const w =
      dynamicStats?.wins ??
      (() => {
        const parts = (product?.ui?.resultSummaries?.[0]?.value || '0-0-0').split('-').map(Number);
        return parts[0] ?? 0;
      })();
    const l =
      dynamicStats?.losses ??
      (() => {
        const parts = (product?.ui?.resultSummaries?.[0]?.value || '0-0-0').split('-').map(Number);
        return parts[1] ?? 0;
      })();
    const t = w + l;
    const pct = t > 0 ? (w / t) * 100 : 0;
    const u =
      dynamicStats != null
        ? dynamicStats.units
        : parseFloat(String(product?.ui?.resultSummaries?.[0]?.units ?? 0));
    return {
      wins: w,
      losses: l,
      total: t,
      record: `${w}–${l}`,
      winPct: `${pct.toFixed(1)}%`,
      units: `${u >= 0 ? '+' : ''}${u.toFixed(2)}u`,
    };
  }, [dynamicStats, product?.ui?.resultSummaries]);

  const nestedReplace = useMemo(() => {
    return function nestedReplaceInner<
      O,
      M extends Map<string, unknown>,
      P extends PriceCalculator,
    >(str: string, option?: O, matrix?: M, calc?: P) {
      if (!str) {
        return str;
      }
      let cur = str;
      let next = str;

      do {
        cur = next;
        next = next.replace(/{([^{}]+)}/g, (_match, inner) => {
          // Handle fallbacks with ||
          const parts = inner.split('||').map((p) => p.trim().replace(/^["']|["']$/g, ''));

          for (const part of parts) {
            // Evaluate this path in various contexts
            const value = (() => {
              if (part === '') {
                return '';
              }

              // Literal fallback text
              if (!/[.[\]]/.test(part) && !/^[a-zA-Z_]/.test(part)) {
                return part;
              }

              const [root, ...rest] = part.split('.');
              const path = rest.join('.');

              const someCalc = calc || calcPrice;

              if (root === 'option' && option) {
                return get(option, path, undefined);
              }
              if (root === 'variety' && variety) {
                return get(variety, path, undefined);
              }
              if (root === 'product' && product) {
                return get(product, path, undefined);
              }
              if (root === 'calc' && someCalc) {
                if (path === 'plays' && (variety?.type === 'season' || someCalc.plays > 300)) {
                  return '';
                }
                return get(someCalc, path, undefined);
              }
              if (root === 'matrix' && matrix) {
                return get(Array.from(matrix.values()), path, undefined);
              }
              if (root === 'this' && option) {
                return path ? get(option, path, undefined) : option;
              }
              if (root === 'stats') {
                return get(statsContext, path, undefined);
              }

              // Try plain key lookup in option as last resort
              return option ? get(option, part, undefined) : undefined;
            })();

            if (value !== undefined && value !== null && value !== '') {
              return value;
            }
          }

          return ''; // fallback if all fail
        });
      } while (next !== cur);

      return next;
    };
  }, [calcPrice, product, variety, statsContext]);

  // "Pay with cryptocurrency" routes to support chat where the order is
  // fulfilled by hand, so the card-checkout attribution capture never runs.
  // Before navigating, stash the buyer's attribution + selected offer on their
  // user record (only when signed in — support chat requires auth anyway) so
  // the admin's manual order form can prefill it. Best-effort: navigate to chat
  // regardless of whether the capture succeeds, and don't block for long.
  const [cryptoBusy, setCryptoBusy] = useState(false);
  const handleCryptoClick = useCallback(
    async (e: React.MouseEvent<HTMLAnchorElement>) => {
      // Let the browser handle modified clicks (new tab, etc.) normally.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) {
        return;
      }
      e.preventDefault();
      if (cryptoBusy) {
        return;
      }
      setCryptoBusy(true);
      try {
        if (meId) {
          await saveCryptoOrderIntent({
            // The offer's TRUE index — the admin's manual order form reads this
            // back to prefill, so a filtered index would prefill a different
            // offer (#2104).
            productId:
              product?.id != null
                ? `${product.id}.${offerCheckoutIndex(variety, selectedIndex)}`
                : undefined,
            productTitle: product?.title,
          });
        }
      } catch {
        // Best-effort — proceed to support chat regardless.
      } finally {
        window.location.href = '/support-chat';
      }
    },
    [cryptoBusy, meId, product?.id, product?.title, selectedIndex, variety],
  );

  return (
    <>
      {/* Promo banner (#1706): announce a held discount/credit BEFORE any option is
          selected, so a buyer with a grant knows it's applied and doesn't bounce on
          seeing the full catalog price. Renders nothing when there's no grant. */}
      <PromoBanner
        promoDiscount={promoDiscount}
        promoCredit={promoCredit}
        productLabel={product?.title}
        locale={locale}
      />
      <div className="grid grid-cols-1 lg:grid-cols-7 lg:items-start lg:gap-x-10">
        <div className="w-full lg:col-span-4">
          {/* Product details */}
          <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
            <div>
              {/* Header band */}
              <div className="px-5 py-6 sm:px-7">
                <div className="flex items-center gap-3">
                  {IconComponent && (
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-zinc-900 dark:bg-zinc-700">
                      <IconComponent className="size-5 fill-white stroke-0" />
                    </div>
                  )}
                  <div>
                    <h1 className="font-bold text-xl text-zinc-950 tracking-tight dark:text-zinc-100">
                      <Skeleton loading={isLoading}>{product?.title || 'Order'}</Skeleton>
                    </h1>
                    {product?.ui?.subtitle && (
                      <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                        {product.ui.subtitle}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex flex-col">
                <div className="flex flex-col">
                  {product?.ui?.href && (
                    <a
                      href={product?.ui?.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex w-fit items-center space-x-1 rounded-full bg-zinc-900/10 px-4 py-2 text-xs text-zinc-700 hover:bg-zinc-900/15 hover:text-zinc-900 dark:bg-white/20 dark:text-white"
                    >
                      <span>{product?.ui?.href}</span>
                      <ArrowTopRightOnSquareIcon
                        height={16}
                        className="size-4 shrink-0"
                        aria-hidden="true"
                      />
                    </a>
                  )}

                  <section aria-labelledby="information-heading">
                    <h2 id="information-heading" className="sr-only">
                      Product information
                    </h2>

                    <div className="font-normal text-sm text-zinc-500 tracking-tight sm:font-medium xl:text-base dark:text-zinc-100">
                      {/* <h3>
                    <Skeleton loading={isLoading}>
                      {nestedReplace(
                        product?.includesDescription ||
                          product?.description ||
                          PRODUCT_DEFAULT_DESCRIPTION[
                            variety?.addOn
                              ? variety?.combos?.length
                                ? 'addOnCombos'
                                : 'addOn'
                              : variety?.combos?.length
                                ? 'combos'
                                : 'default'
                          ],
                      )}
                    </Skeleton>
                  </h3> */}
                      {product?.ui?.releaseTimes?.duration && (
                        <div className="mt-8 mb-6 flex flex-row items-start space-x-4 sm:mb-10">
                          <CalendarDaysIcon className="inline-flex size-6 shrink-0" />
                          <div className="font-bold text-sm text-zinc-700 dark:text-white">
                            {product?.ui?.releaseTimes?.duration}
                          </div>
                        </div>
                      )}
                      {product?.ui?.includes && (
                        <>
                          <hr className="mx-5 my-0 border-zinc-950/10 sm:mx-7 dark:border-white/10" />
                          <div className="px-5 py-5 sm:px-7">
                            <ul
                              role="list"
                              className="flex flex-col gap-y-3 text-xs text-zinc-700 sm:text-sm dark:text-zinc-300"
                            >
                              {product.ui.includes
                                // Scope a bullet to specific packages via `onlyPackages`
                                // (e.g. show the FCS record only on FCS / FBS+FCS).
                                // Bullets without it show for every package.
                                .filter((highlight: any) => {
                                  const onlyPackages =
                                    highlight && typeof highlight === 'object'
                                      ? highlight.onlyPackages
                                      : undefined;
                                  return (
                                    !Array.isArray(onlyPackages) ||
                                    (!!packageKey && onlyPackages.includes(packageKey))
                                  );
                                })
                                .map((highlight: any, idx: number) => (
                                  <li key={idx} className="flex items-start gap-x-3">
                                    <CheckIcon
                                      className="-mt-0.5 size-5 flex-shrink-0 text-emerald-500"
                                      aria-hidden="true"
                                    />
                                    {typeof highlight === 'string' ? (
                                      <span className="leading-snug">
                                        {nestedReplace(highlight)}{' '}
                                        {/* This will replace {calc.plays} */}
                                      </span>
                                    ) : highlight.href ? (
                                      <span className="leading-snug">
                                        {nestedReplace(highlight.text)}
                                        <a
                                          href={highlight.href}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-zinc-900 underline hover:text-zinc-700 dark:text-white dark:hover:text-zinc-200"
                                        >
                                          {highlight.linkText}
                                        </a>
                                      </span>
                                    ) : (
                                      <span className="leading-snug">
                                        {nestedReplace(highlight.text)}
                                      </span>
                                    )}
                                  </li>
                                ))}
                            </ul>
                          </div>
                        </>
                      )}

                      {product?.ui?.releaseTimes?.firstRelease && (
                        <div className="flex flex-row items-start space-x-4 px-5 pt-1 pb-5 sm:px-7">
                          <CalendarDaysIcon className="inline-flex size-6 shrink-0" />
                          <div>
                            <div className="font-medium text-sm text-zinc-700 dark:text-zinc-200">
                              {product?.ui?.releaseTimes?.firstReleaseText ||
                                'First possible release'}
                            </div>
                            <div className="font-bold text-base text-black dark:text-white">
                              {product?.ui?.releaseTimes?.firstRelease}
                            </div>
                          </div>
                        </div>
                      )}
                      {product?.ui?.currentResults && (
                        <div className="mt-10">
                          <div className="prose-sm mt-4 text-zinc-500 dark:text-zinc-400">
                            <dl className="grid w-auto grid-flow-row grid-cols-1 gap-0 space-y-4 lg:grid-cols-1 lg:gap-0 lg:space-y-4">
                              {product?.ui?.currentResults.map(
                                ({ label, value, percentage, units }) => (
                                  <div key={label} className="flex flex-col text-left">
                                    <div className="flex flex-row items-start space-x-4">
                                      <CalendarDaysIcon className="inline-flex size-6 shrink-0" />
                                      <div>
                                        <dt className="mt-0 font-bold text-sm text-zinc-700 dark:text-white">
                                          <span>{label}</span>
                                        </dt>
                                        <dd className="mt-2 p-0 font-semibold text-xl text-zinc-900 tracking-tight dark:text-white">
                                          {value}{' '}
                                          <span className="font-normal text-sm text-zinc-400">
                                            ({percentage}%)
                                          </span>
                                          <span className="ms-2 w-fit whitespace-nowrap rounded bg-emerald-100 px-2.5 py-0.5 font-medium text-emerald-900 text-sm dark:bg-emerald-400/20 dark:text-emerald-50">
                                            {units} units
                                          </span>
                                        </dd>
                                      </div>
                                    </div>
                                  </div>
                                ),
                              )}
                            </dl>
                          </div>
                        </div>
                      )}
                    </div>
                  </section>
                </div>
              </div>

              {packagesMode && packageOffers.length > 0 && (
                <OrderErrorBoundary compact>
                  <hr className="mx-5 my-0 border-zinc-950/10 sm:mx-7 dark:border-white/10" />
                  <div className="px-5 pt-5 sm:px-7">
                    <RadioGroup
                      value={`${selectedIndex}`}
                      onChange={(v: string) => setSelectedIndex(Number(v))}
                      aria-label="Package"
                      className="mb-6 sm:mb-10"
                    >
                      <Label className="w-full truncate font-medium text-xs text-zinc-800 dark:text-zinc-200">
                        {(product?.ui as any)?.text?.packages || 'Choose your package'}
                      </Label>
                      {/* Groups stacked so the main packages and the short intro are
                        visible together. Only a group with a heading shows one; the
                        heading-less main packages render as a plain grid. */}
                      <div className="mt-2 mb-4 flex flex-col gap-5">
                        {durationSections.map((section) => (
                          <div key={section.heading ?? 'main'}>
                            {!!section.heading && (
                              <div className="mb-2 flex items-baseline gap-2">
                                <span className="font-semibold text-sm text-zinc-900 dark:text-white">
                                  {section.heading}
                                </span>
                                {!!section.sharedTerm && (
                                  <span className="text-xs text-zinc-400 dark:text-zinc-500">
                                    · {section.sharedTerm}
                                  </span>
                                )}
                              </div>
                            )}
                            <div
                              className={clsx(
                                'grid grid-cols-1 gap-3',
                                section.offers.length === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-3',
                              )}
                            >
                              {section.offers.map(({ offer, index }) => {
                                const pkgCalc = calculatePrice({
                                  discount,
                                  stats: stats || BASE_STATS,
                                  variety: offer,
                                });
                                const earlyBird = (offer.ui as any)?.earlyBird || 0;
                                const regularPrice = pkgCalc.price + earlyBird;
                                // CFB intro -> season upgrade (#1491): when the server has
                                // stamped offer.upgrade for this buyer, pkgCalc.price is
                                // already the upgraded price (season minus intro paid).
                                // Strike the full season price and badge it as an upgrade.
                                const upgrade = offer.upgrade;
                                const isUpgrade = !!upgrade && typeof upgrade.price === 'number';
                                const seasonFullPrice = upgrade
                                  ? upgrade.price + (upgrade.introPaid || 0)
                                  : 0;
                                // Promotional discount grant (#1706): net it out of this
                                // card's price so a held discount reads at a glance
                                // (e.g. $1,650 struck to $1,000), not only in the final
                                // total. The shared helper skips subscriptions and flat
                                // win-back/upgrade offers, so those cards are untouched.
                                const cardDiscount = promoDiscountDollars(
                                  pkgCalc.price,
                                  offer,
                                  promoDiscount,
                                );
                                const discountedPrice = pkgCalc.price - cardDiscount;
                                const fmtUSD = (n: number) =>
                                  new Intl.NumberFormat(locale, {
                                    style: 'currency',
                                    currency: 'USD',
                                    maximumFractionDigits: 0,
                                  }).format(n);
                                return (
                                  <Field key={index} className="h-full">
                                    <Radio as={Fragment} value={`${index}`}>
                                      {({ checked }) => (
                                        <div
                                          className={clsx(
                                            'flex h-full cursor-pointer flex-col justify-between rounded-lg p-4 ring-1 transition',
                                            checked
                                              ? 'bg-emerald-50 ring-2 ring-emerald-500 dark:bg-emerald-400/10 dark:ring-emerald-400'
                                              : 'bg-white ring-zinc-200 hover:ring-zinc-300 dark:bg-white/5 dark:ring-white/10 dark:hover:ring-white/20',
                                          )}
                                        >
                                          <div className="flex items-center gap-2">
                                            <div className="font-semibold text-sm text-zinc-900 dark:text-white">
                                              {(offer.ui as any)?.coverage ??
                                                (offer.ui as any)?.title ??
                                                `Option ${index + 1}`}
                                            </div>
                                            {isUpgrade && (
                                              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-[10px] text-emerald-700 uppercase tracking-wide dark:bg-emerald-400/20 dark:text-emerald-300">
                                                <SparklesIcon className="size-3 shrink-0" />
                                                Upgrade
                                              </span>
                                            )}
                                          </div>
                                          {isUpgrade ? (
                                            <div className="mt-1 text-xs text-emerald-700 dark:text-emerald-300">
                                              {fmtUSD(upgrade?.introPaid || 0)} intro credit applied
                                            </div>
                                          ) : (
                                            !!(offer.ui as any)?.subtitle && (
                                              <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-300">
                                                {(offer.ui as any).subtitle}
                                              </div>
                                            )
                                          )}
                                          <div className="mt-3 flex items-baseline gap-2">
                                            <span
                                              className={clsx(
                                                'font-bold text-lg',
                                                cardDiscount > 0
                                                  ? 'text-emerald-600 dark:text-emerald-400'
                                                  : 'text-zinc-900 dark:text-white',
                                              )}
                                            >
                                              {fmtUSD(discountedPrice)}
                                            </span>
                                            {cardDiscount > 0 ? (
                                              <span className="text-xs text-zinc-400 line-through dark:text-zinc-500">
                                                {fmtUSD(pkgCalc.price)}
                                              </span>
                                            ) : isUpgrade ? (
                                              <span className="text-xs text-zinc-400 line-through dark:text-zinc-500">
                                                {fmtUSD(seasonFullPrice)}
                                              </span>
                                            ) : (
                                              !!earlyBird && (
                                                <span className="text-xs text-zinc-400 line-through dark:text-zinc-500">
                                                  {fmtUSD(regularPrice)}
                                                </span>
                                              )
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </Radio>
                                  </Field>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </RadioGroup>
                  </div>
                </OrderErrorBoundary>
              )}

              {!packagesMode && !!matrices && matrices.size > 0 && (
                <OrderErrorBoundary compact>
                  <hr className="mx-5 my-0 border-zinc-950/10 sm:mx-7 dark:border-white/10" />
                  <div className="px-5 pt-5 sm:px-7">
                    {Array.from(matrices)
                      .sort(([keyA], [keyB]) => {
                        const keyOrder = Array.from(OFFER_MATRIX_KEYS);
                        const indexA = keyOrder.indexOf(keyA);
                        const indexB = keyOrder.indexOf(keyB);

                        // If key not found in order, put it at the end
                        const finalIndexA = indexA === -1 ? 999 : indexA;
                        const finalIndexB = indexB === -1 ? 999 : indexB;

                        return finalIndexA - finalIndexB;
                      })
                      .map(([key, matrix]) => {
                        let lastPrice: string | number = Infinity;
                        let curPriceIndex = 0;

                        if (key === 'time.subscription.interval') {
                          return (
                            <RadioGroup
                              value={`${JSON.stringify(get(variety, key))}`}
                              onChange={triggerSetSelections(
                                key as keyof typeof OFFER_MATRIX_MAP,
                                matrix,
                              )}
                              aria-label="Subscription frequency"
                              className="mb-8 grid w-fit grid-cols-2 gap-x-1 rounded-full p-1 text-center font-semibold text-xs leading-5 ring-1 ring-zinc-200 ring-inset dark:ring-zinc-400"
                            >
                              {Array.from(matrix).map(([optionKey, map]) => {
                                const { option } = map;
                                const { label } = getFrequencyLabels(option as string);
                                return (
                                  <Field
                                    key={optionKey}
                                    className="flex min-w-32 items-center gap-2 xl:min-w-40"
                                  >
                                    <Radio as={Fragment} value={optionKey}>
                                      {({ checked }) => (
                                        <span
                                          className={clsx(
                                            'w-full cursor-pointer rounded-full px-2.5 py-1',
                                            checked
                                              ? 'bg-emerald-400/20 text-emerald-800 dark:bg-zinc-200 dark:text-black'
                                              : 'text-zinc-500 dark:text-zinc-200',
                                          )}
                                        >
                                          {label}
                                        </span>
                                      )}
                                    </Radio>
                                  </Field>
                                );
                              })}
                            </RadioGroup>
                          );
                        }

                        if (key === 'addOn' && variety?.ui?.hideAddOns === true) {
                          const hasComboProduct62 = Array.isArray(variety?.combos)
                            ? variety.combos.some((combo) => combo.productId === 62)
                            : false;

                          // Return the message if hasComboProduct62, otherwise return null
                          return hasComboProduct62 ? (
                            <div className="my-8 flex size-full cursor-pointer flex-col items-start justify-between space-y-2 rounded-sm bg-white p-3 font-semibold text-sm text-zinc-900 shadow-sm ring-1 ring-zinc-500 sm:flex-1 dark:bg-white/10 dark:text-white">
                              <div className="w-full">
                                <div className="flex w-full flex-row items-center justify-between">
                                  <span>Season Pass (4 Phases + Extras)</span>
                                </div>
                                <div className="mt-2 font-normal text-xs text-zinc-700 dark:text-zinc-200">
                                  All 2025-26 CBB picks released by RAS. 4 phases + Season long
                                  extra service.
                                </div>
                              </div>
                              <div className="mt-1 font-normal text-xs text-zinc-500 dark:text-zinc-300">
                                Estimated volume 320-410 picks
                              </div>
                            </div>
                          ) : null;
                        }

                        return (
                          <>
                            <RadioGroup
                              key={key}
                              value={`${JSON.stringify(get(variety, key))}`}
                              onChange={triggerSetSelections(
                                key as keyof typeof OFFER_MATRIX_MAP,
                                matrix,
                              )}
                              aria-label={key}
                              className="group mb-6 sm:mb-10"
                            >
                              <Label className="w-full truncate font-medium text-xs text-zinc-800 dark:text-zinc-200">
                                {product?.ui?.text?.[key] ||
                                  OFFER_MATRIX_TITLES[key as keyof typeof OFFER_MATRIX_TITLES] ||
                                  key}
                              </Label>

                              {!!(
                                product?.ui?.text?.[`${key}Subtitle`] ||
                                OFFER_MATRIX_TITLES[
                                  `${key}Subtitle` as keyof typeof OFFER_MATRIX_TITLES
                                ]
                              ) && (
                                <p className="py-2 text-xs text-zinc-500 italic dark:text-zinc-300">
                                  {product?.ui?.text?.[`${key}Subtitle`] ||
                                    OFFER_MATRIX_TITLES[
                                      `${key}Subtitle` as keyof typeof OFFER_MATRIX_TITLES
                                    ]}
                                </p>
                              )}
                              <div
                                className={clsx(
                                  'mt-2 mb-4 grid grid-cols-2 justify-items-stretch gap-3',
                                  matrix.size === 3 &&
                                    'md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3',
                                )}
                              >
                                {Array.from(matrix).map(([optionKey, map]) => {
                                  const { option, indexes } = map;
                                  const kt = key as keyof typeof OFFER_MATRIX_MAP;
                                  const offerMatrixMap = OFFER_MATRIX_MAP[kt];
                                  const offerIndex = indexes.has(selectedIndex)
                                    ? selectedIndex
                                    : getBestOfferMatch(kt, matrix, optionKey, variety);

                                  const offer = offers?.[offerIndex];
                                  const over = option as
                                    | API.MembershipProductOfferAddOn
                                    | undefined;

                                  const thisOffer = offer || BASE_OFFER;

                                  const isAddOn = kt === 'addOn' || kt === 'combos';
                                  const remoteProductId = isAddOn ? over && over.productId : false;
                                  const remoteOfferId = (over && over.offerId) || 0;
                                  const remoteProduct =
                                    (!!remoteProductId && product?.products?.[remoteProductId]) ||
                                    undefined;
                                  const remoteOffer =
                                    (remoteProduct && remoteProduct?.offers?.[remoteOfferId]) ||
                                    undefined;

                                  const offerCalc = calculatePrice({
                                    discount,
                                    stats: stats || BASE_STATS,
                                    variety: thisOffer || BASE_OFFER,
                                  });

                                  const remoteCalc = calculatePrice({
                                    discount,
                                    stats: remoteProduct?.stats || BASE_STATS,
                                    variety: remoteOffer || BASE_OFFER,
                                  });

                                  const visiblePrice = isAddOn
                                    ? offerCalc.addOnPrice && offerCalc?.addOnPrice > 0
                                      ? `${isAddOn ? '+' : ''}${formatNumber(
                                          offerCalc?.addOnPrice || 0,
                                          {
                                            maximumFractionDigits: 0,
                                          },
                                        )}`
                                      : '-'
                                    : `${isAddOn ? '+' : ''}${formatNumber(offerCalc.basePrice, {
                                        maximumFractionDigits: 0,
                                      })}`;

                                  let lastPriceIndex = curPriceIndex;

                                  if (lastPrice !== visiblePrice) {
                                    if (lastPrice !== Infinity) {
                                      curPriceIndex++;
                                      lastPriceIndex++;
                                    }
                                    lastPrice = visiblePrice;
                                  }

                                  return (
                                    <Field key={optionKey}>
                                      <Radio as={Fragment} value={optionKey}>
                                        {({ checked }) => {
                                          // const priceDiff = offerCalc.price - (calcPrice.price || 0);

                                          return (
                                            <span
                                              className={clsx(
                                                // active
                                                //   ? 'ring-2 ring-emerald-500 ring-offset-2 dark:ring-offset-zinc-800'
                                                //   : '',
                                                checked
                                                  ? 'bg-white shadow-sm ring-zinc-500 dark:bg-white/10 dark:text-white'
                                                  : 'bg-zinc-900/5 ring-zinc-300 ring-inset hover:bg-zinc-900/2.5 dark:bg-white/2.5 dark:text-zinc-200 dark:ring-white/20 dark:hover:bg-white/5',
                                                'flex size-full cursor-pointer flex-col items-start justify-between space-y-2 rounded-sm p-3 font-semibold text-sm text-zinc-900 ring-1 sm:flex-1',
                                              )}
                                            >
                                              <div className="w-full">
                                                <div className="flex w-full flex-row items-center justify-between">
                                                  <span>
                                                    {(() => {
                                                      const templateString =
                                                        offer?.ui?.option?.[key]?.title ||
                                                        (over
                                                          ? over.ui?.title ||
                                                            offer?.ui?.title ||
                                                            offerMatrixMap?.[0]
                                                          : offerMatrixMap?.[1] || `No ${key}`);

                                                      // Handle any template that contains {this} with plays fallback
                                                      if (
                                                        templateString &&
                                                        templateString.includes('{this}') &&
                                                        templateString.includes('plays||"plays"')
                                                      ) {
                                                        const calcPlays = offerCalc?.plays;

                                                        if (offer?.type === 'season') {
                                                          return offer.ui?.title || 'Full season';
                                                        }
                                                        if (calcPlays && calcPlays > 300) {
                                                          const playCount = option; // Use the option value (60, 10) for high tracking numbers
                                                          return `${playCount} plays`;
                                                        } else {
                                                          // Show the prorated calc.plays for normal cases
                                                          return `${calcPlays || 0} plays`;
                                                        }
                                                      }

                                                      // For all other cases, use nestedReplace
                                                      return nestedReplace(
                                                        templateString,
                                                        over,
                                                        matrix,
                                                        remoteCalc || offerCalc,
                                                      );
                                                    })()}
                                                  </span>
                                                  <CheckCircleIcon
                                                    height={20}
                                                    className={clsx(
                                                      'ml-1 size-5 shrink-0 fill-emerald-700 dark:fill-emerald-500',
                                                      !checked && 'invisible',
                                                    )}
                                                  />
                                                </div>
                                                <div className="mt-2 font-normal text-xs text-zinc-700 dark:text-zinc-200">
                                                  {nestedReplace(
                                                    offer?.ui?.option?.[key]?.description ||
                                                      (over
                                                        ? over?.ui?.description ||
                                                          offer?.ui?.description
                                                        : offerMatrixMap?.[2] || `No ${key}`),
                                                    over,
                                                    matrix,
                                                    remoteCalc || offerCalc,
                                                  )}
                                                </div>
                                              </div>
                                              {key === 'plays' && offer?.ui?.estimatedVolume && (
                                                <div className="mt-1 font-normal text-xs text-zinc-500 dark:text-zinc-300">
                                                  Estimated volume: {offer.ui.estimatedVolume}
                                                </div>
                                              )}
                                              <>
                                                {discount ? (
                                                  <div className="font-normal text-base text-zinc-500 line-through">
                                                    {isAddOn
                                                      ? offerCalc?.originalAddOnPrice
                                                        ? offerCalc?.originalAddOnPrice > 0 &&
                                                          formatNumber(
                                                            offerCalc?.originalAddOnPrice || 0,
                                                            {
                                                              maximumFractionDigits: 0,
                                                            },
                                                          )
                                                        : ''
                                                      : formatNumber(offerCalc.originalPrice, {
                                                          maximumFractionDigits: 0,
                                                        })}
                                                  </div>
                                                ) : (
                                                  ''
                                                )}
                                                <div className="flex w-full flex-row items-baseline justify-between">
                                                  <div
                                                    className="hidden text-base group-has-[[data-priceindex='1']]:block"
                                                    data-priceindex={lastPriceIndex}
                                                  >
                                                    {visiblePrice}
                                                    {offer?.ui?.comboSavings && (
                                                      <span className="text-red-500 text-xs">
                                                        {' '}
                                                        &ndash; Save{' '}
                                                        {new Intl.NumberFormat(locale, {
                                                          style: 'currency',
                                                          currency: 'USD',
                                                          maximumFractionDigits: 0,
                                                        }).format(offer?.ui?.comboSavings)}
                                                      </span>
                                                    )}
                                                  </div>
                                                  {key === 'plays' && offer?.ui?.prorated && (
                                                    <div className="text-right font-normal text-xs text-zinc-700 italic dark:text-zinc-200">
                                                      Prorated on {offer?.ui?.prorated}
                                                    </div>
                                                  )}
                                                </div>
                                              </>
                                              {offer?.ui?.option?.[key]?.schedule ? (
                                                <div className="flex flex-row items-start space-x-2">
                                                  <CalendarIcon
                                                    height={16}
                                                    className="size-4 shrink-0"
                                                  />
                                                  <span className="font-normal text-xs">
                                                    {nestedReplace(
                                                      offer?.ui?.option?.[key]?.schedule ||
                                                        (over
                                                          ? over?.ui?.schedule ||
                                                            offer?.ui?.schedule
                                                          : offerMatrixMap?.[2] || `No ${key}`),
                                                      over,
                                                      matrix,
                                                      remoteCalc || offerCalc,
                                                    )}
                                                  </span>
                                                </div>
                                              ) : (
                                                ''
                                              )}
                                            </span>
                                          );
                                        }}
                                      </Radio>
                                    </Field>
                                  );
                                })}
                              </div>
                            </RadioGroup>
                            {key === 'addOn' && !!addOnProduct && (
                              <div className="-mt-4 mb-8 flex flex-row items-start font-semibold text-sm">
                                <span className="text-zinc-700 dark:text-zinc-200">
                                  Looking for just{' '}
                                  {nestedReplace(
                                    addOnProduct.title || variety.addOn?.ui?.title || '',
                                  )}
                                  ?{' '}
                                </span>
                                <a
                                  href={`/order/${addOnProduct.id}`}
                                  className="ms-2 inline-flex flex-row items-center text-emerald-500 hover:text-emerald-400"
                                >
                                  Order it here
                                  <ArrowRightIcon className="ms-1 inline-block size-4" />
                                </a>
                              </div>
                            )}
                          </>
                        );
                      })}
                  </div>
                </OrderErrorBoundary>
              )}

              <hr className="mx-5 my-0 border-zinc-950/10 sm:mx-7 dark:border-white/10" />
              <div className="px-5 py-6 sm:px-7">
                {(variety?.addOn?.price ?? 0) > 0 && (
                  <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 font-semibold text-emerald-700 text-xs dark:bg-emerald-400/20 dark:text-emerald-300">
                    <SparklesIcon className="size-3.5 shrink-0" />
                    {product?.ui?.comboLabel || 'FBS + FCS combo'}
                  </div>
                )}
                {/* Guard the price breakdown on its own so a display error here
                    still leaves the buy button (below, outside this boundary)
                    usable. The charged amount is recomputed server-side, so a
                    fallback here never risks a wrong charge. */}
                <OrderErrorBoundary compact>
                  <PriceInfo
                    {...calcPrice}
                    loading={isLoading || !variety}
                    earlyBird={variety?.ui?.earlyBird}
                    locale={locale}
                    variety={variety}
                    stats={stats}
                    discount={discount}
                    balance={balance}
                    promoCredit={promoCredit}
                    promoDiscount={promoDiscount}
                    winback={variety?.winback}
                    upgrade={variety?.upgrade}
                    earlyBirdEndDate={variety?.ui?.earlyBirdEndDate}
                  />
                </OrderErrorBoundary>

                {variety?.ui?.prorated ? (
                  <p className="mb-4 text-xs text-zinc-700 dark:text-zinc-200">
                    Prorated on {variety?.ui?.prorated}
                  </p>
                ) : !variety?.time?.subscription?.interval ? (
                  <div className="mb-4">
                    <p className="text-xs text-zinc-500 sm:text-sm dark:text-zinc-400">
                      {(variety?.addOn?.price ?? 0) > 0
                        ? `${termLabel} combo · One-time payment`
                        : (variety?.ui as any)?.isPreseasonOnly
                          ? 'Preseason · One-time payment'
                          : `${termLabel} · One-time payment`}
                    </p>
                    {termNote ? (
                      <p className="mt-0.5 text-xs text-zinc-400 dark:text-zinc-500">{termNote}</p>
                    ) : null}
                  </div>
                ) : null}

                <div className="flex flex-col gap-3">
                  <StripeButton
                    product={product}
                    index={selectedIndex}
                    offer={variety}
                    price={calcPrice.price}
                    size="large"
                    source={pageContext.urlPathname}
                  />
                  {isAnnualSubscription && (
                    <span className="text-sm text-zinc-700 dark:text-zinc-200">
                      Billed{' '}
                      {new Intl.NumberFormat(locale, {
                        style: 'currency',
                        currency: 'USD',
                        maximumFractionDigits: 0,
                      }).format(calcPrice.price)}{' '}
                      annually*
                    </span>
                  )}
                  {!isSubscription && (
                    <div className="flex flex-col items-center gap-3">
                      <a
                        href="/support-chat"
                        onClick={handleCryptoClick}
                        aria-busy={cryptoBusy}
                        className="flex items-center gap-1.5 text-xs text-zinc-400 transition hover:text-zinc-600 dark:text-zinc-500 dark:hover:text-zinc-300"
                      >
                        <CrytpoIcon height={14} className="size-3.5 shrink-0" />
                        <span>Pay with cryptocurrency</span>
                      </a>
                      <a
                        href="/support-chat"
                        className="flex items-center gap-1.5 text-xs text-zinc-400 transition hover:text-zinc-600 dark:text-zinc-500 dark:hover:text-zinc-300"
                      >
                        <ChatBubbleLeftIcon className="size-3.5 shrink-0" />
                        <span>Questions? Chat with us</span>
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* {product?.ui?.resultSummaries && (
              <div className="mt-10 border-zinc-900/10 border-t pt-10 first:mt-0 first:border-t-0 first:pt-0 dark:border-zinc-50/10">
                <div className="prose-sm mt-4 text-zinc-500 dark:text-zinc-400">
                  <dl className="grid w-auto grid-flow-row grid-cols-1 gap-0 space-y-4 lg:grid-cols-1 lg:gap-0 lg:space-y-4">
                    {product?.ui?.resultSummaries.map(({ label, value, percentage, units }) => (
                      <div key={label} className="flex flex-col text-left">
                        <dt className="font-medium text-xs text-zinc-700 dark:text-zinc-300">
                          {label}
                        </dt>
                        <dd className="mt-2 p-0 font-semibold text-xl text-zinc-900 tracking-tight dark:text-white">
                          {value}{' '}
                          <span className="font-normal text-sm text-zinc-400">({percentage}%)</span>
                          <span className="ms-2 w-fit whitespace-nowrap rounded bg-emerald-100 px-2.5 py-0.5 font-medium text-emerald-900 text-sm dark:bg-emerald-400/20 dark:text-emerald-50">
                            {units} units
                          </span>
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
                <div className="mt-6 text-xs text-zinc-500 sm:mt-10 dark:text-zinc-400">
                  Results third party verified by{' '}
                  <a
                    href="https://betstamp.app/u/RAS"
                    target="_blank"
                    className="text-zinc-600 underline dark:text-zinc-300"
                    rel="noreferrer"
                  >
                    betstamp.
                  </a>
                </div>
              </div>
            )} */}

              {product?.ui?.note ? (
                <div className="mt-4 mb-6 rounded-sm bg-amber-50 p-4 text-amber-700 sm:mt-10 dark:bg-amber-300/20 dark:text-amber-400">
                  <div className="flex space-x-2">
                    <InformationCircleIcon height={20} className="size-5 shrink-0" />
                    <div className="font-normal text-sm">{product?.ui?.note}</div>
                  </div>
                </div>
              ) : (
                ''
              )}
            </div>
            {/* Mailing list sign up form — integrated at bottom of card */}
            <hr className="mx-5 my-0 border-zinc-950/10 sm:mx-7 dark:border-white/10" />
            <div className="order-last mt-0 bg-zinc-50 px-5 py-5 sm:px-7 dark:bg-zinc-800/50">
              <p className="font-medium text-xs text-zinc-600 dark:text-zinc-300">
                Not ready to sign up?
              </p>
              <p className="mt-0.5 text-xs text-zinc-400 dark:text-zinc-500">
                Get free picks and service announcements.
              </p>
              <div className="mt-3">
                <MailingListForm compact guarded={false} autoFocus={false} />
              </div>
            </div>
          </div>
          {hasBettingTalkExtra && (
            <></>
            // <div className="order-last mt-10 rounded-sm bg-sky-50 p-4 text-sky-950 shadow-xs ring-1 ring-sky-900/10 lg:col-span-4 dark:bg-sky-950 dark:text-sky-50 dark:ring-sky-50/5">
            //   <span className="pb-2 font-medium text-sm">Added bonus</span>
            //   <div className="mt-2 flex flex-row items-center justify-start space-x-4">
            //     <BettingTalkIcon className="size-12 fill-sky-900 dark:fill-sky-200" />
            //     <div>
            //       <h4 className="font-semibold">Betting Talk membership</h4>
            //       {/* <p className="text-sm font-normal">(up to $295 value)</p> */}
            //     </div>
            //   </div>

            //   <ul className="mt-4 ml-4 list-disc space-y-1 text-xs">
            //     <li>Access to Betting Talk private community</li>
            //     <li>Access to exclusive NFL content and discussion from Adam Chernoff</li>
            //     <li>
            //       Access to exclusive CFB/NBA content and discussion from Eddie Walls, including his
            //       popular blog posts
            //     </li>
            //     <li>Access to the entire RAS team and other long time sports betting pros</li>
            //   </ul>
            // </div>
          )}
        </div>
        {/* Product details */}
        <div className="mt-10 lg:col-span-3 lg:mt-0">
          {product?.ui?.releaseTimes && (
            <>
              {/* firstRelease is shown in the left product card (see above), so it is
                intentionally not repeated here in the right details column. */}
              {product?.ui?.releaseTimes?.nextRelease && (
                <div className="mt-4 mb-6 flex flex-row items-start space-x-4 sm:mb-10">
                  <CalendarDaysIcon className="inline-flex size-6 shrink-0" />
                  <div>
                    <div className="text-xs text-zinc-700 dark:text-zinc-200">
                      Next possible release
                    </div>
                    <div className="font-medium text-sm">
                      {product?.ui?.releaseTimes?.nextRelease}
                    </div>
                  </div>
                </div>
              )}
              {product?.ui?.releaseTimes?.times && (
                <div className="mt-10 border-zinc-900/10 border-t pt-10 first:mt-0 first:border-t-0 first:pt-0 dark:border-zinc-50/10">
                  <h3 className="font-medium text-sm text-zinc-900 dark:text-zinc-100">
                    Release schedule
                  </h3>
                  {product?.ui?.releaseTimes?.times && (
                    <div className="prose-sm text-zinc-500 dark:text-zinc-400">
                      <dl className="grid w-auto grid-flow-row grid-cols-1 gap-0 space-y-4 lg:grid-cols-1 lg:gap-0 lg:space-y-4">
                        {product?.ui?.releaseTimes?.times.map(({ label, description }) => (
                          <div key={label} className="flex flex-col text-left">
                            <dt className="font-medium text-xs text-zinc-700 dark:text-zinc-300">
                              {label}
                            </dt>
                            <dd className="mt-2 p-0 font-semibold text-xl text-zinc-900 tracking-tight dark:text-white">
                              {description}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {product?.ui?.phases && (
            <div className="mt-10 border-zinc-900/10 border-t pt-10 first:mt-0 first:border-t-0 first:pt-0 dark:border-zinc-50/10">
              <h3 className="font-medium text-sm text-zinc-900 dark:text-zinc-100">
                Phase Schedule
              </h3>
              <ul className="space-y-4">
                {product?.ui?.phases.map((phase) => {
                  // const isCurrent = phase === currentPhase;
                  return (
                    <div
                      className={`flex w-full flex-row items-start space-x-2 rounded`}
                      key={phase.title}
                    >
                      <CalendarDaysIcon className="inline-block size-4" />
                      <li
                        key={phase.title}
                        className="w-full border-zinc-950/10 border-b border-dotted pb-1 dark:border-zinc-white/10"
                      >
                        <div className="flex flex-row justify-between">
                          <div>
                            <div className="font-semibold text-xs">
                              <span className="text-sm">{phase.title}</span>
                              <span className="ml-2 font-normal text-zinc-500">
                                {(() => {
                                  return new Date(phase.start).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                    timeZone: 'UTC',
                                  });
                                })()} – {(() => {
                                  return new Date(phase.end).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                    timeZone: 'UTC',
                                  });
                                })()}
                              </span>
                            </div>
                            <div className="text-xs text-zinc-700 dark:text-zinc-400">
                              Estimated {phase.volume} picks
                            </div>
                          </div>
                          <span className="font-semibold text-base text-zinc-900 tracking-tight dark:text-zinc-100">
                            ${phase.price.toLocaleString()}
                          </span>
                        </div>
                        {/* {isCurrent && proratedPrice !== null && (
                              <span className="ml-2 text-green-700">
                                (Join now for ${proratedPrice.toLocaleString()} prorated)
                              </span>
                            )} */}
                      </li>
                    </div>
                  );
                })}
              </ul>
            </div>
          )}

          {product?.ui?.newThisYear && (
            <div className="mt-10 border-zinc-900/10 border-t pt-10 first:mt-0 first:border-t-0 first:pt-0 dark:border-zinc-50/10">
              <h3 className="font-medium text-emerald-700 text-sm dark:text-zinc-100">
                New for {product.ui?.newThisYear?.year}
                <SparklesIcon height={16} className="ml-2 inline-block size-4 shrink-0" />
              </h3>
              <div className="prose prose-sm mt-4 text-zinc-500 dark:text-zinc-400">
                <ul role="list">
                  {product.ui?.newThisYear?.list.map((highlight) => (
                    <li key={highlight}>{highlight}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {product?.ui?.whatToExpect && (
            <div className="mt-10 border-zinc-900/10 border-t pt-10 first:mt-0 first:border-t-0 first:pt-0 dark:border-zinc-50/10">
              <h3 className="font-medium text-sm text-zinc-900 dark:text-zinc-100">
                Service details
              </h3>
              <div className="prose prose-sm mt-4 text-zinc-500 dark:text-zinc-400">
                <ul role="list">
                  {product.ui?.whatToExpect.map((highlight) => (
                    <li key={highlight}>{highlight}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {product?.ui?.includesGroups && (
            <div className="mt-10 border-zinc-900/10 border-t pt-10 first:mt-0 first:border-t-0 first:pt-0 dark:border-zinc-50/10">
              <h3 className="font-medium text-sm text-zinc-900 dark:text-zinc-100">
                Possible release types
              </h3>
              <div className="prose prose-sm mt-4 text-zinc-500 dark:text-zinc-400">
                {product.ui?.includesGroups.map((group) => (
                  <div key={group?.title}>
                    <h3 className="font-medium text-sm text-zinc-900 dark:text-zinc-100">
                      {group?.title}
                    </h3>
                    <ul role="list">
                      {group?.items.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {product?.ui?.highlights && (
            <div className="mt-10 border-zinc-900/10 border-t pt-10 first:mt-0 first:border-t-0 first:pt-0 dark:border-zinc-50/10">
              <h3 className="font-medium text-sm text-zinc-900 dark:text-zinc-100">Highlights</h3>
              <div className="prose prose-sm mt-4 text-zinc-500 dark:text-zinc-400">
                <ul role="list">
                  {product.ui?.highlights.map((highlight) => (
                    <li key={highlight}>{highlight}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* RAS+ note hidden while RAS+ is not offered
        {product?.ui?.rasPlusNote ? (
          <div className="mt-4 mb-6 rounded-sm bg-emerald-50 p-4 text-emerald-700 first:mt-0 sm:mt-10 dark:bg-emerald-300/20 dark:text-emerald-400">
            <div className="flex space-x-2">
              <RASPlusIcon className="size-6 shrink-0 fill-emerald-800 dark:fill-emerald-400" />
              <div className="font-normal text-sm">
                {product?.ui?.rasPlusNote}{' '}
                <a
                  href="/order/55"
                  className="group inline-flex items-center font-semibold text-emerald-700 dark:text-emerald-400"
                >
                  <span>Learn more</span>
                  <ChevronRightIcon height={16} className="size-4 shrink-0 group-hover:hidden" />
                  <ArrowRightIcon
                    height={16}
                    className="hidden size-4 shrink-0 group-hover:inline-block"
                  />
                </a>
              </div>
            </div>
          </div>
        ) : (
          ''
        )}
        */}
          {product?.ui?.resultSummaries || dynamicStats ? (
            // Stat-card view — shows headline numbers for the selected range
            (() => {
              // --- Range filtering ---
              // When dynamic season rows are available, slice to the selected range and re-aggregate
              const rangeRows =
                dynamicSeasonRows && statsRange !== 'all'
                  ? dynamicSeasonRows.slice(0, statsRange as number)
                  : dynamicSeasonRows;

              const rangeStats = rangeRows
                ? {
                    wins: rangeRows.reduce((s, r) => s + r.wins, 0),
                    losses: rangeRows.reduce((s, r) => s + r.losses, 0),
                    units: rangeRows.reduce((s, r) => s + r.units, 0),
                  }
                : null;

              // When a range view is configured (not 'all'), hide static all-time numbers while
              // the dynamic API call is in flight so they don't flash before the filtered data loads.
              const statsStillLoading = productStatsRequest.loading && !dynamicStats;
              // Mask static numbers while a filtered (range or coverage) query is loading, and
              // never fall back to the full-coverage static fields when a coverage filter is active.
              const maskStatics =
                (statsRange !== 'all' && statsStillLoading) ||
                (coverageActive && statsStillLoading);
              const suppressStatic = maskStatics || coverageActive;

              // Prefer range-filtered dynamic stats; fall back to static ui fields (always all-time,
              // full coverage) — but only when no coverage filter is active.
              const wins =
                rangeStats?.wins ??
                dynamicStats?.wins ??
                (suppressStatic
                  ? 0
                  : (() => {
                      const summary = product.ui?.resultSummaries?.[0];
                      const parts = (summary?.value || '0-0-0').split('-').map(Number);
                      return parts[0] ?? 0;
                    })());
              const losses =
                rangeStats?.losses ??
                dynamicStats?.losses ??
                (suppressStatic
                  ? 0
                  : (() => {
                      const summary = product.ui?.resultSummaries?.[0];
                      const parts = (summary?.value || '0-0-0').split('-').map(Number);
                      return parts[1] ?? 0;
                    })());
              const total = wins + losses;
              const winPct = total > 0 ? (wins / total) * 100 : 0;
              const unitsVal =
                rangeStats != null
                  ? rangeStats.units
                  : dynamicStats != null
                    ? dynamicStats.units
                    : suppressStatic
                      ? 0
                      : parseFloat(String(product.ui?.resultSummaries?.[0]?.units ?? 0));
              // CLV is always all-time regardless of range; suppressed when a coverage filter is active.
              const clv =
                dynamicClv ?? (coverageActive ? null : (product.ui?.clvPercentage ?? null));

              // Season breakdown table — respect the selected range filter
              const tableRows = rangeRows ?? dynamicSeasonRows;
              // Curated statRows are intentional — show them even when there's only one row.
              // Dynamically-derived rows need 2+ so a single row doesn't just echo the headline.
              const isStaticRows = !tableRows;
              const seasonRows: Array<{ label: string; record: string; pct: string }> | null =
                tableRows
                  ? tableRows.map((r) => {
                      const t = r.wins + r.losses;
                      return {
                        label: r.label,
                        record: `${r.wins}–${r.losses}`,
                        pct: t > 0 ? ((r.wins / t) * 100).toFixed(1) : '0.0',
                      };
                    })
                  : coverageActive
                    ? null // static statRows are full-coverage; don't show under a coverage filter
                    : (product.ui?.statRows?.map((r) => ({
                        label: r.label,
                        record: r.record,
                        pct: r.pct,
                      })) ?? null);

              // --- Subtitle label ---
              const gameTypeLabel = statsGameTypes?.length
                ? statsGameTypes
                    .map((g) => (g === 'extra' ? 'Extra' : g === 'fcs' ? 'FCS' : g))
                    .join('/') + ' results'
                : null;
              const staticLabel = product?.ui?.resultSummaries?.[0]?.label ?? null;
              const SPORT_NAMES: Record<number, string> = { 1: 'CFB', 2: 'CBB', 3: 'NFL' };
              const sportPrefix = product?.sportIds
                ?.map((id) => SPORT_NAMES[id])
                .filter(Boolean)
                .join('/');
              const rangeSuffix =
                statsRange === 'all'
                  ? 'all time'
                  : `last ${statsRange} season${(statsRange as number) !== 1 ? 's' : ''}`;
              const recordLabel = coverageActive
                ? `${coverageLabel ?? `${sportPrefix ? sportPrefix + ' ' : ''}results`} · ${rangeSuffix}`
                : gameTypeLabel
                  ? `${sportPrefix ? sportPrefix + ' ' : ''}${gameTypeLabel} · ${rangeSuffix}`
                  : staticLabel
                    ? statsRange !== 'all' && dynamicSeasonRows
                      ? `${staticLabel} · ${rangeSuffix}`
                      : staticLabel
                    : `All-time record`;

              // Range toggle button labels
              const rangeLabel = (r: 'all' | number) => (r === 'all' ? 'All time' : `Last ${r}`);

              return (
                <div className="mt-6 overflow-hidden rounded-2xl border border-zinc-200 first:mt-0 dark:border-zinc-700">
                  {/* Track record header */}
                  <div className="flex items-start justify-between px-5 py-5 sm:px-7">
                    <div>
                      <h3 className="font-semibold text-zinc-950 dark:text-zinc-100">
                        Track record
                      </h3>
                      {product?.ui?.resultsLink?.href ? (
                        <a
                          href={(() => {
                            const base =
                              statsRange !== 'all' && product.ui.resultsLink.rangeHref
                                ? ((product.ui.resultsLink.rangeHref as Record<string, string>)[
                                    String(statsRange)
                                  ] ?? product.ui.resultsLink.href)
                                : product.ui.resultsLink.href;
                            // Scope the results link to the selected package's play types
                            // (e.g. FCS/Extra → ?gameType=fcs,extra) so it opens a filtered view.
                            if (coverageGameTypes?.length) {
                              return `${base}${base.includes('?') ? '&' : '?'}gameType=${coverageGameTypes.join(',')}`;
                            }
                            return base;
                          })()}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-0.5 block text-xs text-zinc-400 underline underline-offset-2 hover:text-zinc-600 dark:hover:text-zinc-300"
                        >
                          {recordLabel}
                        </a>
                      ) : (
                        <p className="mt-0.5 text-xs text-zinc-400">{recordLabel}</p>
                      )}
                    </div>
                    {/* Range toggle — only shown when multiple options are configured and dynamic data is available */}
                    {statsRangeOptions.length > 1 && dynamicSeasonRows && (
                      <div className="flex shrink-0 overflow-hidden rounded-lg border border-zinc-200 font-medium text-xs dark:border-zinc-700">
                        {statsRangeOptions.map((opt) => (
                          <button
                            key={String(opt)}
                            type="button"
                            onClick={() => setStatsRange(opt)}
                            className={clsx(
                              'px-3 py-1.5 transition-colors',
                              statsRange === opt
                                ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900'
                                : 'bg-transparent text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200',
                            )}
                          >
                            {rangeLabel(opt)}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Stats — 2×2 grid */}
                  <hr className="mx-5 my-0 border-zinc-950/10 sm:mx-7 dark:border-white/10" />
                  <div className="grid grid-cols-2 gap-x-4 px-5 py-4 sm:px-7">
                    <div className="py-2">
                      <p className="font-semibold text-[10px] text-zinc-400 uppercase tracking-widest dark:text-zinc-500">
                        Record
                      </p>
                      <p className="mt-1 whitespace-nowrap font-bold text-xl text-zinc-950 tabular-nums tracking-tight dark:text-white">
                        <Skeleton loading={maskStatics}>
                          {wins}
                          <span className="mx-0.5 font-normal">–</span>
                          {losses}
                        </Skeleton>
                      </p>
                    </div>

                    <div className="py-2">
                      <p className="font-semibold text-[10px] text-zinc-400 uppercase tracking-widest dark:text-zinc-500">
                        Win rate
                      </p>
                      <p className="mt-1 font-bold text-emerald-500 text-xl tabular-nums tracking-tight">
                        <Skeleton loading={maskStatics}>{winPct.toFixed(1)}%</Skeleton>
                      </p>
                    </div>

                    {(unitsVal !== 0 || maskStatics) && (
                      <div className="py-2">
                        <p className="font-semibold text-[10px] text-zinc-400 uppercase tracking-widest dark:text-zinc-500">
                          Units won
                        </p>
                        <p className="mt-1 font-bold text-emerald-500 text-xl tabular-nums tracking-tight">
                          <Skeleton loading={maskStatics}>
                            {unitsVal >= 0 ? '+' : ''}
                            {unitsVal.toFixed(2)}u
                          </Skeleton>
                        </p>
                      </div>
                    )}

                    {(clv != null || maskStatics) && (
                      <div className="py-2">
                        <p className="font-semibold text-[10px] text-zinc-400 uppercase tracking-widest dark:text-zinc-500">
                          Beat close
                        </p>
                        <p className="mt-1 font-bold text-xl text-zinc-950 tabular-nums tracking-tight dark:text-white">
                          <Skeleton loading={maskStatics}>{clv}%</Skeleton>
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Season breakdown table */}
                  {seasonRows &&
                    (seasonRows.length > 1 || (isStaticRows && seasonRows.length > 0)) && (
                      <>
                        <hr className="mx-5 my-0 border-zinc-950/10 sm:mx-7 dark:border-white/10" />
                        <div className="px-5 py-4 sm:px-7">
                          <table className="w-full">
                            <thead>
                              <tr>
                                <th className="pb-2 text-left font-semibold text-[10px] text-zinc-400 uppercase tracking-widest dark:text-zinc-500" />
                                <th className="pb-2 text-right font-semibold text-[10px] text-zinc-400 uppercase tracking-widest dark:text-zinc-500">
                                  Record
                                </th>
                                <th className="pb-2 text-right font-semibold text-[10px] text-zinc-400 uppercase tracking-widest dark:text-zinc-500">
                                  Win %
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-50 dark:divide-zinc-800">
                              {seasonRows.map((row) => (
                                <tr key={row.label}>
                                  <td className="py-1.5 text-xs text-zinc-500 dark:text-zinc-400">
                                    {row.label}
                                  </td>
                                  <td className="py-1.5 text-right font-semibold text-xs text-zinc-900 tabular-nums dark:text-zinc-100">
                                    {row.record}
                                  </td>
                                  <td className="py-1.5 text-right font-semibold text-emerald-500 text-xs tabular-nums">
                                    {row.pct}%
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}

                  {/* betstamp footer */}
                  <hr className="mx-5 my-0 border-zinc-950/10 sm:mx-7 dark:border-white/10" />
                  <div className="px-5 py-3 sm:px-7">
                    <a
                      href="https://betstamp.app/u/RAS"
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-zinc-400 leading-snug underline underline-offset-2 hover:text-zinc-600 dark:hover:text-zinc-300"
                    >
                      Results independently verified by{' '}
                      <span className="whitespace-nowrap">betstamp →</span>
                    </a>
                  </div>
                </div>
              );
            })()
          ) : product?.ui?.results ? (
            // Fallback: year-by-year table for products without resultSummaries
            <div className="mt-10 border-zinc-900/10 border-t pt-10 first:mt-0 first:border-t-0 first:pt-0 dark:border-zinc-50/10">
              <div className="mx-auto flex w-full flex-col sm:w-full lg:flex lg:flex-auto lg:justify-end">
                <h3 className="font-medium text-sm text-zinc-900 dark:text-zinc-100">
                  {variety?.sport === 'nfl' ? 'Past seasons & totals results' : 'Past results'}
                </h3>
                <span className="my-3 text-xs text-zinc-700 dark:text-zinc-200">
                  Results third party verified by{' '}
                  <a
                    href="https://betstamp.app/u/RAS"
                    target="_blank"
                    className="text-zinc-900 underline dark:text-white/70"
                    rel="noreferrer"
                  >
                    betstamp
                  </a>
                  .
                </span>
                <Table>
                  <TableHeader className="hidden md:contents">
                    <TableRow>
                      <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 pl-4 text-left font-semibold text-sm sm:pr-3 sm:pl-0 dark:bg-transparent dark:text-white">
                        Season
                      </TableHead>
                      <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 pl-4 text-left font-semibold text-sm sm:pr-3 sm:pl-0 dark:bg-transparent dark:text-white">
                        Record
                      </TableHead>
                      <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 pl-4 text-left font-semibold text-sm sm:pr-3 sm:pl-0 dark:bg-transparent dark:text-white">
                        Units
                      </TableHead>
                      <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 pl-4 text-left font-semibold text-sm sm:pr-3 sm:pl-0 dark:bg-transparent dark:text-white">
                        Win rate
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {product?.ui?.results.map(({ label, value, percentage, units }) => (
                      <TableRow
                        key={label}
                        className="font-normal hover:group-[&]/tbody:bg-initial"
                      >
                        <TableCell className="w-1/4 whitespace-nowrap py-2 pl-0 font-semibold text-sm first-letter:uppercase sm:pr-3 dark:text-zinc-200">
                          <span className="inline-block first-letter:uppercase">
                            {`${label || '-'}`.replace(/-+/g, ' ')}
                          </span>
                        </TableCell>
                        <TableCell className="w-1/4 whitespace-nowrap py-2 pl-0 text-sm sm:pr-3 dark:text-zinc-200">
                          {value}
                        </TableCell>
                        <TableCell
                          className={clsx(
                            'w-1/4 whitespace-nowrap py-2 pl-0 text-sm sm:pr-3 dark:text-zinc-200',
                            units >= 0 ? 'text-emerald-500' : 'text-red-400',
                          )}
                        >
                          {units >= 0 ? '+' : ''}
                          {toFixed(units)}
                        </TableCell>
                        <TableCell className="w-1/4 whitespace-nowrap py-2 pl-0 text-sm sm:pr-3 dark:text-zinc-200">
                          {toFixed(percentage)}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : null}

          {visibleFaqs.length > 0 && (
            <div className="mt-6 overflow-hidden rounded-2xl border border-zinc-200 first:mt-0 dark:border-zinc-700">
              <div className="px-5 py-5 sm:px-7">
                <h3 className="font-semibold text-zinc-950 dark:text-zinc-100">
                  Frequently asked questions
                </h3>
              </div>
              <div>
                {visibleFaqs.map((faq) => (
                  <Fragment key={faq.question}>
                    <hr className="mx-5 my-0 border-zinc-950/10 sm:mx-7 dark:border-white/10" />
                    <details className="group px-5 py-4 sm:px-7">
                      <summary className="flex cursor-pointer list-none items-center justify-between font-medium text-sm text-zinc-900 dark:text-zinc-100">
                        {faq.question}
                        <ChevronDownIcon className="size-4 shrink-0 text-zinc-400 transition-transform duration-200 group-open:rotate-180" />
                      </summary>
                      <p className="mt-3 text-sm text-zinc-500 leading-relaxed dark:text-zinc-400">
                        {faq.answer}
                      </p>
                    </details>
                  </Fragment>
                ))}
              </div>
            </div>
          )}

          {disclaimer.summary && (
            <p className="mt-6 px-1 text-xs text-zinc-400 leading-relaxed dark:text-zinc-500">
              {disclaimer.summary}
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default ProductPage;
